import React from 'react';
import { Text } from 'wix-ui-tpa/cssVars';
import { FavoriteFillSmall } from '@wix/wix-ui-icons-common/on-stage';

import { MenuItem } from '../../../../types/menu';
import type { ResolvedItemAvailability } from '../../../../utils/menuAvailability';

import { classes, st } from './DishItem.st.css';
import { dishItemDataHooks } from './constants';
import { useHooks } from './useHooks';
import { LabelIcon } from './LabelIcon';

type DishItemProps = {
  item: MenuItem;
  menuId: string;
  sectionId: string;
  availability?: ResolvedItemAvailability;
  onClick: (
    item: MenuItem,
    menuId: string,
    sectionId: string,
    availability?: ResolvedItemAvailability,
  ) => void;
  cartQuantity?: number;
  discountLabel?: string;
  discountedPrice?: string;
  className?: string;
};

const DishItem: React.FC<DishItemProps> = ({
  item,
  menuId,
  sectionId,
  availability,
  onClick,
  cartQuantity = 0,
  discountLabel,
  discountedPrice,
  className,
}) => {
  const {
    layoutSize,
    isAvailable,
    isCardLayout,
    isImageLeft,
    shownLabels,
    extraLabelCount,
    shouldShowImage,
    shouldShowDescription,
    shouldShowLabels,
    shouldShowFeatured,
    shouldShowLabelsRow,
    handleClick,
    handleKeyDown,
    priceText,
    imageUrl,
    imageAlt,
    featuredLabel,
  } = useHooks({ item, menuId, sectionId, availability, onClick });

  return (
    <div
      className={st(
        classes.root,
        {
          [layoutSize]: true,
          unavailable: !isAvailable,
          card: isCardLayout,
          imageLeft: isImageLeft,
        },
        className,
      )}
      data-hook={dishItemDataHooks.root()}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      role="button"
      tabIndex={0}
      aria-haspopup="true"
    >
      <div className={classes.contentRow}>
        <div className={classes.textContent}>
          <div className={classes.topContent}>
            <div className={classes.titleRow}>
              {cartQuantity > 0 && (
                <span
                  className={classes.cartBadge}
                  data-hook={dishItemDataHooks.cartBadge()}
                >
                  {cartQuantity}
                </span>
              )}
              <Text
                className={classes.title}
                data-hook={dishItemDataHooks.title()}
                tagName="p"
              >
                {item.name}
              </Text>
            </div>
            <div className={classes.descriptionBlock}>
              {shouldShowDescription && (
                <Text
                  className={classes.description}
                  data-hook={dishItemDataHooks.description()}
                  tagName="p"
                >
                  {item.description}
                </Text>
              )}
              {shouldShowLabelsRow && (
                <div
                  className={classes.labelsRow}
                  data-hook={dishItemDataHooks.labels()}
                >
                  {shouldShowFeatured && (
                    <span
                      className={classes.featuredBadge}
                      data-hook={dishItemDataHooks.featuredBadge()}
                      aria-label={featuredLabel}
                    >
                      <FavoriteFillSmall />
                    </span>
                  )}
                  {shouldShowLabels && (
                    <>
                      {shownLabels.map((label) => (
                        <LabelIcon
                          key={label.id}
                          className={classes.labelIcon}
                          url={label.icon!.url ?? label.icon!.id!}
                          alt={label.name}
                        />
                      ))}
                      {extraLabelCount > 0 && (
                        <Text
                          className={classes.labelCounter}
                          data-hook={dishItemDataHooks.labelCounter()}
                          tagName="span"
                        >
                          +{extraLabelCount}
                        </Text>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
          <div className={classes.priceBlock}>
            {discountedPrice ? (
              <div className={classes.discountedPriceRow}>
                <Text
                  className={classes.originalPrice}
                  data-hook={dishItemDataHooks.price()}
                  tagName="span"
                >
                  {priceText}
                </Text>
                <Text className={classes.price} tagName="span">
                  {discountedPrice}
                </Text>
              </div>
            ) : (
              <Text
                className={classes.price}
                data-hook={dishItemDataHooks.price()}
                tagName="p"
              >
                {priceText}
              </Text>
            )}
            {discountLabel && (
              <span
                className={classes.discountLabel}
                data-hook={dishItemDataHooks.discountLabel()}
              >
                {discountLabel}
              </span>
            )}
          </div>
        </div>
        {shouldShowImage && (
          <div className={classes.imageWrapper}>
            <img
              className={classes.image}
              src={imageUrl}
              alt={imageAlt}
              data-hook={dishItemDataHooks.image()}
              loading="lazy"
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default DishItem;
